import TableHeaderControls from "@components/Project/TableHeaderControls";
import AdvancedSearch from "@components/Search/AdvancedSearch";
import { useAdvancedSearch } from "@hooks/useAdvancedSearch";
import { resolveSearchCriteria } from "@utils/searchHelpers";
import TableWithPagination from "@components/Tables/TableWithPagination";
import { Breadcrumb } from "@components/UI";
import queryKeys from "@configs/react-query/query-key";
import ToastConstant from "@configs/react-toastify/snackbar";
import { FLAG_OPTIONS, TOAST } from "@constants/index";
import { useFetchData } from "@hooks/useFetchData";
import { useMutationData } from "@hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { formatDate, formatDateTime, uppercase } from "@utils/common";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

const TEST_ID = "component-cve-status";
const tid = (suffix) => `${TEST_ID}-${suffix}`;
const fieldContainerTestId = (name) => tid(`field-${name}-container`);
const fieldLabelTestId = (name) => tid(`field-${name}-label`);
const fieldValueTestId = (name) => tid(`field-${name}-value`);

export default function ComponentCVEStatus() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { componentCveId } = useParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [cveData, setCveData] = useState(null);
  const [searchFilters, setSearchFilters] = useState({});
  const [componentCVEList, setComponentCVEList] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedRows, setSelectedRows] = useState([]);
  const [mostCommonLicense, setMostCommonLicense] = useState("");
  const isInitialMount = useRef(true);
  const prevCurrentPage = useRef(1);
  const [debouncedSearchFilters, setDebouncedSearchFilters] = useState(searchFilters);
  const debounceTimerRef = useRef(null);
  const [isSearching, setIsSearching] = useState(false);


  // API call for component cve details
  const queryKey = [queryKeys.getCVEStatusDetail, componentCveId];
  const queryUrl = queryKeys.getCVEStatusDetail + "/" + componentCveId;

  const {
    data,
    isLoading,
    isError,
    error,
  } = useFetchData(
    queryKey,
    queryUrl,
    {
      method: "GET",
    },
    {},
    {
      enabled: !!componentCveId,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );


  useEffect(() => {
    if (isError && error?.response?.status === 404) {
      navigate("/404", { state: { backTo: "/cve-status" } });
    }
  }, [isError, error, navigate]);
  // Process project data when it's available
  useEffect(() => {
    if (data && !isLoading && !error) {
      const response = data;
      if (typeof response !== "string") {
        setCveData(response);
      }
    }
  }, [data, isLoading, error]);

  useEffect(() => {
    if (cveData) {
      setSearchFilters({
        cveId: cveData?.cve || "",
        componentName: cveData?.component || "",
        componentVersion: cveData?.componentVersion || "",
      })
    }
  }, [cveData])


  const getComponentCVEStatusList = useMutationData(
    queryKeys.getComponentCVEStatusList,
    { method: "POST" },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setComponentCVEList(response.object);
          setTotalData(response.objectCount);
          setTotalPages(Math.ceil(response.objectCount / itemsPerPage));

          if (response.object && response.object.length > 0) {
            const licenseCount = {};
            response.object.forEach(item => {
              if (item.licenseName) {
                licenseCount[item.licenseName] = (licenseCount[item.licenseName] || 0) + 1;
              }
            });

            const mostCommon = Object.entries(licenseCount).reduce((a, b) =>
              licenseCount[a[0]] > licenseCount[b[0]] ? a : b
            );

            setMostCommonLicense(mostCommon ? mostCommon[0] : "");
          }
        }
      },
      onError: (error) => {
        console.error("Error during get data:", error);
      },
    },
  );

  const fetchData = useCallback(() => {
    if (getComponentCVEStatusList.isPending) {
      return;
    }

    const effectiveCriteria = resolveSearchCriteria(searchFilters, {
      quickSearchField: "keyword",
      isAdvancedPanelOpen: showAdvancedSearch,
    });

    const bodyRequest = Object.keys(effectiveCriteria).reduce((acc, key) => {
      let value = effectiveCriteria[key];
      if (typeof value === "string") {
        value = value.trim();
      }
      if (value !== "" && value !== undefined) {
        // Format date fields using formatDate function
        if (key === "actedAtFrom" || key === "actedAtTo" || key === "createdAtFrom" || key === "createdAtTo") {
          acc[key] = formatDate(value);
        } else {
          acc[key] = value;
        }
      }
      return acc;
    }, {});

    bodyRequest.pageSize = itemsPerPage;
    bodyRequest.startRow = itemsPerPage * (currentPage - 1);

    getComponentCVEStatusList.mutate(bodyRequest);
  }, [searchFilters, currentPage, itemsPerPage, getComponentCVEStatusList.isPending]);

  useEffect(() => {
    if (
      searchFilters &&
      typeof searchFilters === 'object' &&
      Object.keys(searchFilters).length > 0
    ) {
      fetchData();
      isInitialMount.current = false;
    }
  }, [debouncedSearchFilters]);

  useEffect(() => {
    if (!isInitialMount.current && prevCurrentPage.current !== currentPage) {
      fetchData();
      prevCurrentPage.current = currentPage;
    }
  }, [currentPage]);

  // Debounce search input to avoid calling API too frequently
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const hasActualChange = Object.values(searchFilters).some((value) => value && value !== "");
    if (hasActualChange) {
      setIsSearching(true);
    }
    if (!isInitialMount.current) {
      debounceTimerRef.current = setTimeout(() => {
        setDebouncedSearchFilters(searchFilters);
        setIsSearching(false);
      }, 500); // Wait 500ms after user stops typing
    } else {
      setDebouncedSearchFilters(searchFilters);
      setIsSearching(false);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchFilters]);

  const columns = [
    "projectId",
    "verifFileName",
    "verificationVersion",
    "cvssVersion",
    "baseScore",
    "baApprovedFlag",
    "baApprovedAt",
    "createdAt",
  ];

  const columnConfig = {
    projectId: {
      style: {
        width: "18%",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("status.project-id"),
    },
    verifFileName: {
      style: {
        width: "18%",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("status.verif-file-name"),
      customRender: (value, row) => {
        return row.binaryRealFileName || row.sourceCodeRealFileName;
      },
    },
    verificationVersion: {
      style: {
        width: "10%",
        whiteSpace: "normal",
        overflowWrap: "initial",
        textAlign: "center",
      },
      headerRender: () => t("status.verification-version"),
    },
    cvssVersion: {
      style: {
        width: "10%",
        whiteSpace: "normal",
        textAlign: "center",
        overflowWrap: "initial",
        padding: "0.5rem",
        verticalAlign: "middle",
      },
      headerRender: () => t("status.cvss-version"),
    },
    baseScore: {
      style: {
        width: "8%",
        whiteSpace: "normal",
        textAlign: "center",
        overflowWrap: "initial",
        padding: "0.5rem",
        verticalAlign: "middle",
      },
      headerRender: () => t("status.score"),
    },
    baApprovedFlag: {
      style: {
        width: "10%",
        whiteSpace: "normal",
        textAlign: "center",
        overflowWrap: "initial",
        verticalAlign: "middle",
      },
      headerRender: () => t("status.approved-flag"),
      customRender: (value, row) => {
        const flagValue = value || row.scaApprovedFlag;
        if (!flagValue) return "-";
        const option = FLAG_OPTIONS.find((opt) => opt.value.toUpperCase() === flagValue.toUpperCase());
        return option ? t(option.label) : flagValue;
      },
    },
    baApprovedAt: {
      style: {
        width: "15%",
        whiteSpace: "normal",
        overflowWrap: "initial",
        verticalAlign: "middle",
        textAlign: "center",
      },
      headerRender: () => t("status.approved-at"),
      customRender: (value, row) => {
        const approvedAt = value || row.scaApprovedAt;
        if (!approvedAt) return "-";
        return formatDateTime(approvedAt);
      },
    },
    createdAt: {
      style: {
        width: "14%",
        whiteSpace: "normal",
        overflowWrap: "initial",
        verticalAlign: "middle",
        textAlign: "center",
        padding: "0.5rem",
      },
      headerRender: () => t("status.created-date"),
      customRender: (value) => formatDateTime(value),
    },
  };

  const searchFields = [
    {
      name: "cveId",
      label: t("status.cve"),
      placeholder: t("status.cve-placeholder"),
    },
    {
      name: "componentName",
      label: t("status.component"),
      placeholder: t("status.component-placeholder"),
    },
    {
      name: "componentVersion",
      label: t("status.component-version"),
      placeholder: t("status.component-version-placeholder"),
    },
    {
      name: "licenseName",
      label: t("status.license"),
      placeholder: t("status.license-placeholder"),
    },
    {
      name: "verificationTool",
      label: t("status.verification-tool"),
      placeholder: t("status.verification-tool-placeholder"),
    },
    {
      name: "projectId",
      label: t("status.project-id"),
      placeholder: t("status.project-id-placeholder"),
    },
    {
      name: "projectName",
      label: t("status.project-name"),
      placeholder: t("status.project-name-placeholder"),
    },
    {
      name: "attackVector",
      label: t("status.attack-vector"),
      placeholder: t("status.attack-vector-placeholder"),
    },
    {
      name: "attackComplexity",
      label: t("status.attack-complexity"),
      placeholder: t("status.attack-complexity-placeholder"),
    },
    {
      name: "availability",
      label: t("status.availability"),
      placeholder: t("status.availability-placeholder"),
    },
    {
      name: "verificationVersion",
      label: t("status.verification-version"),
      placeholder: t("status.verification-version-placeholder"),
      type: "number",
    },
    {
      name: "clarityBinaryId",
      label: t("status.clarity-binary-id"),
      placeholder: t("status.clarity-binary-id-placeholder"),
    },
    {
      name: "fossidScanCode",
      label: t("status.fossid-scan-code"),
      placeholder: t("status.fossid-scan-code-placeholder"),
    },
  ];
  const { showAdvancedSearch, handleAdvancedSearchToggle } = useAdvancedSearch({
    searchFields: searchFields,
    quickSearchField: "keyword",
    setSearchCriteria: setSearchFilters,
  });

  useEffect(() => {
    fetchData();
  }, [showAdvancedSearch]);

  const onRowClick = (row) => {
    const routes = {
      CLARITY: `../projects/${row.projectSeq}/clarity/${row.binaryVerificationSeq}`,
      FOSSID: `../projects/${row.projectSeq}/fossid/${row.sourceCodeVerificationSeq}`,
      CLARITYAIR: `../projects/${row.projectSeq}/clarity-air/${row.clarityAirVerificationSeq}`,
    };

    if (routes[row.verificationTool]) {
      navigate(routes[row.verificationTool]);
    }
  };

  const searchHandler = useCallback((inputData) => {
    setSearchFilters(prev => ({
      ...prev,
      ...inputData,
    }));
  }, [searchFields]);

  const handleItemsPerPageChange = useCallback((newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
  }, []);

  return (
    <div className={`flex flex-col ${colors.bgSecondary}`} data-testid={tid("page")}>
      {/* Header with Breadcrumbs */}
      <div data-testid={tid("header")}>
        <div className="space-y-2">
          <Breadcrumb
            data-testid={tid("breadcrumb")}
            items={[
              { label: t("status.cve-information-list-breadcrumb"), href: "/cve-status" },
              { label: t("status.component-cve-status-title") },
            ]}
          />
          <div>
            <h1 className={`text-2xl font-bold ${colors.textPrimary}`} data-testid={tid("subtitle")}>
              {/* {getComponentCVEStatusList.isPending || !cveData ? "Loading..." : cveData.cve} */}
            </h1>
          </div>
        </div>
      </div>
      {/* Header */}
      <div className={`${colors.textPlaceholder} py-1 pb-6`} data-testid={tid("title-section")}>
        <div className="flex items-center justify-between">
          <h1 className={`text-[28px] uppercase font-bold ${colors.textPrimary}`} data-testid={tid("title")}>
            {t("status.component-cve-status-title")}
          </h1>
        </div>
      </div>

      {/* Controls */}
      <div data-testid={tid("info-section")} className={`border ${colors.borderPrimary} ${colors.bgPrimary} rounded-2xl p-6`}>
        <div className={`border ${colors.borderPrimary} ${colors.bgSecondary} rounded-2xl p-4`} data-testid={tid("info-summary")}>
          <div className="grid grid-cols-4 gap-4 text-sm" data-testid={tid("info-grid")}>
            <div data-testid={fieldContainerTestId("cve")}>
              <span className={`font-medium ${colors.textSecondary}`} data-testid={fieldLabelTestId("cve")}>CVE</span>
              <div className={`mt-1 ${colors.textPrimary}`} data-testid={fieldValueTestId("cve")}>{cveData?.cve || "-"}</div>
            </div>
            <div data-testid={fieldContainerTestId("component")}>
              <span className={`font-medium ${colors.textSecondary}`} data-testid={fieldLabelTestId("component")}>{t("status.component")}</span>
              <div className={`mt-1 ${colors.textPrimary}`} data-testid={fieldValueTestId("component")}>{cveData?.component || "-"}</div>
            </div>
            <div data-testid={fieldContainerTestId("component-version")}>
              <span className={`font-medium ${colors.textSecondary}`} data-testid={fieldLabelTestId("component-version")}>{t("common.component-version")}</span>
              <div className={`mt-1 ${colors.textPrimary}`} data-testid={fieldValueTestId("component-version")}>{cveData?.componentVersion || "-"}</div>
            </div>
            <div data-testid={fieldContainerTestId("license-name")}>
              <span className={`font-medium ${colors.textSecondary}`} data-testid={fieldLabelTestId("license-name")}>{t("status.license-name")}</span>
              <div className={`mt-1 ${colors.textPrimary}`} data-testid={fieldValueTestId("license-name")}>{mostCommonLicense || "-"}</div>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between pt-6" data-testid={tid("usage-header")}>
          <h3 className={`font-semibold ${colors.textPrimary}`} data-testid={tid("usage-title")}>{t("status.component-usage-by-projects")}</h3>
        </div>
        <div className="pt-6" data-testid={tid("table-controls")}>
          <TableHeaderControls
          onAdvancedSearchToggle={handleAdvancedSearchToggle}
          onSearch={searchHandler}
            hasSelectedRows={selectedRows.length > 0}
            searchField="keyword"
          searchValue={searchFilters.keyword ?? ""}
            placeholderSearch={t("common.search-placeholder")}
            isDisplayAddButton={false}
            isDisplayDeleteButton={false}
            isSearching={isSearching}
            controlsTestId={tid("table-header-controls")}
            searchInputTestId={tid("search-input")}
            advancedSearchButtonTestId={tid("advanced-search-button")}
          />
        </div>

        {/* Advanced Search */}
        {showAdvancedSearch && (
          <div data-testid={tid("advanced-search-wrapper")}>
            <AdvancedSearch
              data-testid={tid("advanced-search")}
              title={t("common.advanced-search")}
              searchFields={[...searchFields]}
              searchHandler={searchHandler}
              withoutSearch={true}
              fixedFields={{
                cveId: cveData?.cve || ""
              }}
            />
          </div>
        )}

        <div className="flex-grow min-h-0" data-testid={tid("table-wrapper")}>
          <TableWithPagination
            columnConfig={columnConfig}
            data={componentCVEList}
            columns={columns}
            onRowClick={onRowClick}
            loading={getComponentCVEStatusList.isPending}
            totalPages={totalPages}
            totalData={totalData}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            maxRowsPerPage={itemsPerPage}
            selectedRows={selectedRows}
            disabledRow={[]}
            shouldShowGoToPage={true}
            onItemsPerPageChange={handleItemsPerPageChange}
            dataTestId={tid("table")}
          />
        </div>
      </div>
    </div>
  );
}
